import React, { useState } from "react";
import {
  TextField,
  Button,
  IconButton,
  Grid,
  Modal,
  Box,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit"; // Import EditIcon

interface PdfFile {
  file: File | null;
  title: string;
}

interface PdfUploadProps {
  onFilesSelect: (files: PdfFile[]) => void;
}

const PdfUpload: React.FC<PdfUploadProps> = ({ onFilesSelect }) => {
  const [uploadedFiles, setUploadedFiles] = useState<PdfFile[]>([]);
  const [open, setOpen] = useState(false); // Modal visibility state
  const [newTitle, setNewTitle] = useState(""); // New title for PDF
  const [newFile, setNewFile] = useState<File | null>(null); // New file to upload
  const [loading] = useState(false); // Loading state for file upload
  const [editIndex, setEditIndex] = useState<number | null>(null); // Track the index of the file being edited

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewFile(file);
  };

  // Handle title input change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTitle(e.target.value);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpen(false);
    setNewTitle("");
    setNewFile(null);
    setEditIndex(null); // Reset edit index on modal close
  };

  // Handle file upload (add to list)
  const handleAddNewFile = () => {
    if (newTitle && newFile) {
      const newFileData: PdfFile = { title: newTitle, file: newFile };
      if (editIndex !== null) {
        const updatedFiles = [...uploadedFiles];
        updatedFiles[editIndex] = newFileData;
        setUploadedFiles(updatedFiles);
        onFilesSelect(updatedFiles);
      } else {
        setUploadedFiles((prevFiles) => [...prevFiles, newFileData]);
        onFilesSelect([...uploadedFiles, newFileData]);
      }
      handleCloseModal();
    }
  };

  // Delete a file entry
  const handleDeleteFile = (index: number) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    onFilesSelect(updatedFiles);
  };

  // Edit a file entry
  const handleEditFile = (index: number) => {
    const fileData = uploadedFiles[index];
    setEditIndex(index); // Set the index of the file being edited
    setNewTitle(fileData.title);
    setNewFile(fileData.file);
    setOpen(true); // Open the modal to edit
  };

  return (
    <div>
      {uploadedFiles.map((fileData, index) => (
        <Grid container spacing={2} alignItems="center" key={index}>
          {/* Title Input */}
          <Grid item xs={12} sm={2}>
            <TextField
              label="عنوان PDF"
              variant="outlined"
              value={fileData.title}
              placeholder="عنوان فایل PDF را وارد کنید"
              disabled
              style={{
                marginBottom: "12px",
              }}
            />
          </Grid>

          {/* File Input */}
          <Grid item xs={12} sm={2}>
            {fileData.file && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "14px",
                  color: "#333",
                  wordWrap: "break-word",
                }}
              >
                {fileData.file.name}
              </div>
            )}
          </Grid>

          {/* Edit Button */}
          <Grid item>
            <IconButton
              color="primary"
              onClick={() => handleEditFile(index)}
              title="ویرایش فایل"
            >
              <EditIcon />
            </IconButton>
          </Grid>

          {/* Delete Button */}
          <Grid item>
            <IconButton
              color="error"
              onClick={() => handleDeleteFile(index)}
              title="حذف فایل"
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      {/* Button to open the modal */}
      <Button
        variant="contained"
        onClick={() => setOpen(true)}
        style={{
          marginTop: "16px",
          padding: "8px 10px",
          borderRadius: "5px",
          backgroundColor: "#9ee1d1",
          color: "#00b087",
          fontWeight: "semibold",
          boxShadow: "none",
        }}
      >
        افزودن فایل جدید
      </Button>

      {/* Modal for File Upload */}
      <Modal
  open={open}
  onClose={handleCloseModal}
  aria-labelledby="modal-title"
  aria-describedby="modal-description"
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 400,
      bgcolor: "background.paper",
      border: "1px solid #00b087",
      borderRadius: "5px",
      p: 4,
    }}
  >
    {/* Close Button */}
    <IconButton
      aria-label="close"
      onClick={handleCloseModal}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        fontSize: 12,
        color: "#00b087",
      }}
    >
      ❌
    </IconButton>

    <TextField
      label="عنوان PDF"
      variant="outlined"
      fullWidth
      value={newTitle}
      onChange={handleTitleChange}
      placeholder="عنوان فایل PDF را وارد کنید"
      style={{
        marginBottom: "16px",
        borderRadius: "5px",
        backgroundColor: "#fff",
        padding: "5px auto",
      }}
    />
    <Button
      variant="outlined"
      component="label"
      fullWidth
      style={{
        borderRadius: "5px",
        padding: "10px 20px",
        backgroundColor: "#9ee1d1",
        color: "#00b087",
        border: "none",
      }}
    >
      انتخاب فایل PDF
      <input
        type="file"
        accept="application/pdf"
        hidden
        onChange={handleFileChange}
      />
    </Button>
    {newFile && (
      <div
        style={{
          fontSize: "14px",
          color: "#333",
        }}
      >
        انتخاب شده: {newFile.name}
      </div>
    )}
    <Button
      variant="contained"
      color="primary"
      onClick={handleAddNewFile}
      style={{
        marginTop: "16px",
        padding: "8px 20px",
        borderRadius: "5px",
      }}
      disabled={loading || !newTitle || !newFile}
    >
      {loading ? (
        <CircularProgress size={24} style={{ color: "#fff" }} />
      ) : (
        "افزودن فایل"
      )}
    </Button>
  </Box>
</Modal>

    </div>
  );
};
 
export default PdfUpload;
